import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { AGENT_AUTH_TOKEN_CHANGED_EVENT, STORAGE_KEYS } from '../constants';
import type { AgentProfile } from '../types';
import { fetchDashboard } from '../services/api';

interface AuthState {
  user: AgentProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (user: AgentProfile, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AGENT_AUTH_TOKEN_CHANGED_EVENT));
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (token && raw) {
      try {
        const user = JSON.parse(raw) as AgentProfile;
        setState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        void fetchDashboard()
          .then((res) => {
            if (!res?.success || !res.data) return;
            const fresh: AgentProfile = {
              ...user,
              emailVerified: !!res.data.email_verified,
              phoneVerified: !!res.data.phone_verified,
            };
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fresh));
            setState((p) => (p.token === token ? { ...p, user: fresh } : p));
          })
          .catch(() => {
            // keep cached user
          });
        return;
      } catch {
        logout();
        return;
      }
    }
    setState((p) => ({ ...p, isLoading: false }));
  }, [logout]);

  const login = useCallback((user: AgentProfile, token: string) => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AGENT_AUTH_TOKEN_CHANGED_EVENT));
    }
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [logout]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
